import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { IconArrowLeft, IconCard } from '../components/icons';
import { CopyButton, Empty, Field, Modal, Skeleton, useAsync, useToast } from '../components/ui';
import { track } from '../lib/activity';
import { api, ApiError, BillingView, canSetUpWorkspace, InvoiceDetail, InvoiceStatus, Plan } from '../lib/api';
import { day, money, num } from '../lib/format';
import { useSession } from '../lib/session';
import { currentLocale, t } from '../lib/i18n';

const TONE: Record<InvoiceStatus, string> = { draft: 'pending', open: 'warn', paid: 'ok', void: 'revoked', uncollectible: 'bad' };
const SUB_LABEL: Record<string, string> = { trialing: 'Пробный период', active: 'Активен', past_due: 'Есть просроченный счёт', canceled: 'Завершён' };

const FEATURE_LABEL: Record<string, string> = {
  after_verification: 'после подтверждения домена',
  log_detective: 'Детектив логов',
  dedicated_ip: 'выделенный IP',
  sla: 'SLA',
};

function planFacts(p: Plan): string[] {
  return [
    t('{n} писем в месяц', { n: num(p.included_emails) }),
    p.daily_limit ? t('до {n} в день', { n: num(p.daily_limit) }) : null,
    p.max_domains ? t('доменов: {n}', { n: p.max_domains }) : t('домены без ограничения'),
    t('журнал {n} дн.', { n: p.log_retention_days }),
    p.overage_cents_per_1000 != null ? t('сверх лимита {sum} / 1 000', { sum: money(p.overage_cents_per_1000, p.currency) }) : null,
    ...p.features.map((f) => t(FEATURE_LABEL[f] ?? f)),
  ].filter(Boolean) as string[];
}

/** Monthly volume as a rising tide: the water line is the share of the included volume. */
function UsageTide({ used, included, over }: { used: number; included: number; over: boolean }) {
  const share = included > 0 ? used / included : 0;
  const fill = Math.min(1, share);
  return (
    <div className={`tide-meter${over ? ' over' : ''}`} role="meter" aria-valuemin={0} aria-valuemax={included} aria-valuenow={used} aria-label={t('Использовано писем за месяц')}>
      <div className="tide-fill" style={{ width: `${Math.max(fill * 100, used ? 1.5 : 0)}%` }}>
        <span className="tide-crest" />
      </div>
      <div className="tide-label">
        <b>{num(used)}</b> {t('из {included} · {pct}%', { included: num(included), pct: Math.round(share * 100) })}
      </div>
    </div>
  );
}

function PlanRequest({ plans, current, onClose }: { plans: Plan[]; current: string | null; onClose: () => void }) {
  const toast = useToast();
  const [planId, setPlanId] = useState(plans.find((p) => p.id !== current && p.price_cents > 0)?.id ?? plans[0]?.id ?? '');
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  async function send() {
    setBusy(true);
    setError(undefined);
    try {
      const r = await api.post<{ ok: true; message: string }>('/billing/plan-request', { plan_id: planId, ...(comment.trim() ? { comment: comment.trim() } : {}) });
      toast(r.message);
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('Не получилось отправить заявку'));
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal
      title={t('Сменить тариф')}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>{t('Отмена')}</button>
          <button className="btn btn-primary" disabled={busy || !planId} onClick={send} data-track="billing.plan_request_send">
            {busy ? t('Отправляем…') : t('Отправить заявку')}
          </button>
        </>
      }
    >
      <div className="stack">
        <p className="muted small">
          {t('Смена тарифа — это заявка менеджеру: он подтвердит условия и выставит счёт. Деньги автоматически не списываются.')}
        </p>
        <div className="plan-pick">
          {plans.map((p) => (
            <label key={p.id} className={`plan-option${planId === p.id ? ' on' : ''}`}>
              <input type="radio" name="plan" value={p.id} checked={planId === p.id} onChange={() => setPlanId(p.id)} />
              <span className="grow">
                <span>
                  <b>{p.name}</b>
                  {p.id === current && <span className="pill ok" style={{ marginLeft: 8 }}>{t('текущий')}</span>}
                </span>
                <small className="muted">{planFacts(p).slice(0, 2).join(' · ')}</small>
              </span>
              <span className="plan-price">{p.price_cents ? `${p.id === 'scale' ? t('от') + ' ' : ''}${money(p.price_cents, p.currency)}` : t('бесплатно')}</span>
            </label>
          ))}
        </div>
        <Field label={t('Комментарий для менеджера')} hint={t('Необязательно: ожидаемый объём, реквизиты компании, удобное время для связи')} error={error}>
          <textarea className="input" rows={3} maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)} />
        </Field>
      </div>
    </Modal>
  );
}

export function BillingPage() {
  const { me } = useSession();
  const data = useAsync(() => api.get<BillingView>('/billing'));
  const [asking, setAsking] = useState(false);
  const nav = useNavigate();
  const manage = !!me && canSetUpWorkspace(me);
  useEffect(() => track('billing.view'), []);
  const b = data.data;

  return (
    <>
      <div className="page-head">
        <div className="grow">
          <h1>{t('Тариф и оплата')}</h1>
          <p>{t('Счёт выставляется вручную, оплата не списывается автоматически. Единица учёта — письмо, принятое провайдером (не получатель).')}</p>
        </div>
        {manage && b && (
          <button className="btn btn-primary" onClick={() => setAsking(true)} data-track="billing.plan_request_click">
            {t('Сменить тариф')}
          </button>
        )}
      </div>

      {data.error && <div className="callout bad">{t('Не удалось загрузить данные о тарифе: {msg}', { msg: data.error.message })}</div>}

      {b?.usage.over_quota && (
        <div className="callout warn" style={{ marginBottom: 16 }}>
          {t('Вы превысили включённый объём. Письма продолжают отправляться; разница войдёт в следующий счёт.')}
        </div>
      )}

      <div className="grid grid-2">
        <div className="card">
          <div className="card-head"><h3>{t('Тариф')}</h3>{b?.subscription && <span className={`pill ${b.subscription.status === 'past_due' ? 'bad' : b.subscription.status === 'trialing' ? 'warn' : 'ok'}`}>{t(SUB_LABEL[b.subscription.status])}</span>}</div>
          <div className="card-pad stack">
            {!b ? (
              <Skeleton h={90} />
            ) : b.plan && b.subscription ? (
              <>
                <div className="row wrap" style={{ alignItems: 'baseline' }}>
                  <span className="plan-name">{b.plan.name}</span>
                  <span className="spacer" />
                  <span className="plan-price big">{b.subscription.price_cents ? t('{sum} / мес.', { sum: money(b.subscription.price_cents, b.subscription.currency) }) : t('бесплатно')}</span>
                </div>
                <div className="chips">{planFacts(b.plan).map((f) => <span key={f} className="chip static">{f}</span>)}</div>
                <p className="small muted">
                  {t('Период: {from} — {to}', { from: day(b.subscription.current_period_start), to: day(b.subscription.current_period_end) })}
                  {b.subscription.trial_ends_at ? t(' · пробный период до {date}', { date: day(b.subscription.trial_ends_at) }) : ''}
                </p>
              </>
            ) : (
              <p className="muted">{t('Тариф не назначен — напишите менеджеру.')}</p>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-head"><h3>{t('Прилив месяца')}</h3><span className="small muted">{b ? b.usage.month.slice(0, 7) : ''}</span></div>
          <div className="card-pad stack">
            {!b ? (
              <Skeleton h={60} />
            ) : (
              <>
                <UsageTide used={b.usage.accepted_emails} included={b.usage.included_emails} over={b.usage.over_quota} />
                <p className="small muted">
                  {t('Считаются письма, принятые провайдером. Тестовые письма мастера онбординга ({n}) в объём не входят.', { n: num(b.usage.test_emails) })}
                  {t('Обновляется каждые 15 минут')}{b.usage.updated_at ? t(', последний пересчёт — {time}', { time: new Date(b.usage.updated_at).toLocaleTimeString(currentLocale(), { hour: '2-digit', minute: '2-digit' }) }) : ''}.
                  {t('Превышение не блокирует отправку.')}
                </p>
              </>
            )}
          </div>
        </div>
      </div>

      {b?.payment_instructions && manage && (
        <div className="card" style={{ marginTop: 16 }}>
          <div className="card-head"><h3>{t('Как оплатить')}</h3>{b.payment_instructions.text && <CopyButton small value={b.payment_instructions.text} label={t('Скопировать реквизиты')} />}</div>
          <div className="card-pad stack">
            <pre className="pay-text">{b.payment_instructions.text}</pre>
            <p className="small muted">{t('Оплату отмечает финансовый отдел MailOcean после поступления перевода — обычно в течение рабочего дня.')}</p>
          </div>
        </div>
      )}

      <div className="card" style={{ marginTop: 16 }}>
        <div className="card-head"><h3>{t('Счета')}</h3></div>
        {!b ? (
          <div className="card-pad stack"><Skeleton /><Skeleton /></div>
        ) : b.finance_visible === false ? (
          <p className="card-pad small muted">{t('Счета, суммы и реквизиты для оплаты видят владелец и администраторы пространства.')}</p>
        ) : !b.invoices.length ? (
          <Empty icon={<IconCard size={36} />} title={t('Счетов пока нет')}>
            {t('На бесплатном тарифе счета не выставляются. После смены тарифа менеджер пришлёт счёт — он появится здесь.')}
          </Empty>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr><th>{t('Номер')}</th><th>{t('Период')}</th><th style={{ textAlign: 'right' }}>{t('Сумма')}</th><th>{t('Статус')}</th><th>{t('Срок оплаты')}</th></tr>
              </thead>
              <tbody>
                {b.invoices.map((i) => (
                  <tr key={i.id} className={manage ? 'click' : ''} onClick={() => manage && nav(`/billing/invoices/${i.id}`)}>
                    <td className="mono">{i.number ?? '—'}</td>
                    <td className="small">{i.period_start ? `${day(i.period_start)} — ${day(i.period_end)}` : '—'}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}><b>{money(i.total_cents, i.currency)}</b></td>
                    <td><span className={`pill ${i.overdue ? 'bad' : TONE[i.status]}`}>{i.overdue ? t('Просрочен') : i.status_label}</span></td>
                    <td className="small muted">{i.status === 'paid' ? t('оплачен {date}', { date: day(i.paid_at) }) : day(i.due_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {b && !manage && b.invoices.length > 0 && <p className="card-pad small muted">{t('Подробности счёта видят владелец и администраторы пространства.')}</p>}
      </div>

      {asking && b && <PlanRequest plans={b.plans} current={b.plan?.id ?? null} onClose={() => setAsking(false)} />}
    </>
  );
}

/** Printable invoice (v1 has no PDF: print this page or save it as PDF from the browser). */
export function InvoicePage() {
  const { id } = useParams();
  const inv = useAsync(() => api.get<InvoiceDetail>(`/billing/invoices/${id}`), [id]);
  const i = inv.data;
  return (
    <>
      <div className="page-head no-print">
        <Link to="/billing" className="btn btn-ghost btn-sm"><IconArrowLeft size={15} /> {t('Тариф и оплата')}</Link>
        <span className="spacer" />
        {i && <button className="btn" onClick={() => window.print()} data-track="billing.invoice_print">{t('Печать / PDF')}</button>}
      </div>
      {inv.error && <div className="callout bad">{inv.error instanceof ApiError && inv.error.status === 404 ? t('Счёт не найден.') : inv.error.message}</div>}
      {!i ? (
        !inv.error && <div className="card card-pad stack"><Skeleton h={30} w={260} /><Skeleton /><Skeleton /></div>
      ) : (
        <article className="card card-pad invoice">
          <header className="row wrap" style={{ alignItems: 'flex-start' }}>
            <div className="grow">
              <div className="small muted">{t('Счёт')}</div>
              <h2 className="mono">{i.number}</h2>
              <div className="small">{t('Плательщик:')} <b>{i.tenant_name}</b></div>
            </div>
            <div className="stack" style={{ gap: 4, textAlign: 'right' }}>
              <span className={`pill ${i.overdue ? 'bad' : TONE[i.status]}`} style={{ alignSelf: 'flex-end' }}>{i.overdue ? t('Просрочен') : i.status_label}</span>
              <span className="small">{t('Выставлен: {date}', { date: day(i.issued_at) })}</span>
              <span className="small">{t('Оплатить до: {date}', { date: day(i.due_at) })}</span>
            </div>
          </header>
          {i.period_start && <p className="small muted">{t('Период: {from} — {to}', { from: day(i.period_start), to: day(i.period_end) })}</p>}
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>{t('Позиция')}</th><th style={{ textAlign: 'right' }}>{t('Кол-во')}</th><th style={{ textAlign: 'right' }}>{t('Цена')}</th><th style={{ textAlign: 'right' }}>{t('Сумма')}</th></tr></thead>
              <tbody>
                {i.lines.map((l) => (
                  <tr key={l.id}>
                    <td>{l.description}</td>
                    <td style={{ textAlign: 'right' }}>{num(l.quantity)}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{money(l.unit_cents, i.currency)}</td>
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>{money(l.amount_cents, i.currency)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="invoice-totals">
            <div className="row"><span className="muted">{t('Подытог')}</span><span className="spacer" /><span>{money(i.subtotal_cents, i.currency)}</span></div>
            <div className="row"><span className="muted">{t('НДС')}</span><span className="spacer" /><span>{t('не облагается в v1')}</span></div>
            <div className="row"><b>{t('Итого')}</b><span className="spacer" /><b>{money(i.total_cents, i.currency)}</b></div>
            {i.paid_cents > 0 && <div className="row"><span className="muted">{t('Оплачено')}</span><span className="spacer" /><span>{money(i.paid_cents, i.currency)}</span></div>}
            {i.amount_due_cents > 0 && <div className="row"><b>{t('К оплате')}</b><span className="spacer" /><b>{money(i.amount_due_cents, i.currency)}</b></div>}
          </div>
          {i.notes && <p className="small">{i.notes}</p>}
          {i.payment_instructions?.text && (
            <section className="stack">
              <h3>{t('Реквизиты для оплаты')}</h3>
              <pre className="pay-text">{i.payment_instructions.text}</pre>
            </section>
          )}
          <p className="small muted">{t('Оплата фиксируется вручную после поступления перевода. Автоматического списания нет.')}</p>
        </article>
      )}
    </>
  );
}
